import React, { useState, useCallback } from 'react';
import Layout from '../Layout';
import Plot from 'react-plotly.js';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.jungleclick.online/api';

/* ──────────────────────────────────────────────── */
/*  Web Traffic — Google Trends Demand Analysis     */
/*  Port of tracking2-main/pages/traffic.py         */
/* ──────────────────────────────────────────────── */

function WebTraffic() {
    const [keyword, setKeyword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);     // { decomposition, seasonalAnalytics, keyword }
    const [showTable, setShowTable] = useState(false);
    const [csvFile, setCsvFile] = useState(null);
    const [csvName, setCsvName] = useState(null);

    const token = localStorage.getItem('accessToken');
    const authHeaders = { Authorization: `Bearer ${token}` };

    // ── Request mode ──
    const handleRequest = useCallback(async () => {
        if (!keyword.trim()) {
            setError('Please enter a keyword to use Request mode.');
            return;
        }
        setError(null);
        setLoading(true);
        setResult(null);
        try {
            const res = await axios.post(
                `${API_BASE}/google-trends/demand-analysis`,
                { keyword: keyword.trim() },
                { headers: { ...authHeaders, 'Content-Type': 'application/json' } }
            );
            if (res.data?.success) {
                setResult(res.data.data);
            } else {
                setError(res.data?.message || 'No data returned.');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Error fetching Google Trends data.');
        } finally {
            setLoading(false);
        }
    }, [keyword, authHeaders]);

    // ── Upload CSV mode ──
    const handleUploadCsv = useCallback(async () => {
        if (!csvFile) {
            setError('Please choose a CSV file exported from Google Trends.');
            return;
        }
        setError(null);
        setLoading(true);
        setResult(null);
        try {
            const formData = new FormData();
            formData.append('file', csvFile);
            const res = await axios.post(
                `${API_BASE}/google-trends/upload-csv`,
                formData,
                { headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' } }
            );
            if (res.data?.success) {
                setResult(res.data.data);
            } else {
                setError(res.data?.message || 'Error parsing CSV.');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Error uploading CSV file.');
        } finally {
            setLoading(false);
        }
    }, [csvFile, authHeaders]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCsvFile(file);
            setCsvName(file.name);
        }
    };

    const handleClearCsv = () => {
        setCsvFile(null);
        setCsvName(null);
    };

    // ── Download decomposition as CSV ──
    const handleDownloadCsv = () => {
        if (!result?.decomposition) return;
        const rows = result.decomposition;
        const headers = ['Date', 'Original', 'Trend', 'Seasonal', 'Residual'];
        const csvContent = [
            headers.join(','),
            ...rows.map(r => [r.displayDate, r.originalValue, r.trend?.toFixed(4), r.seasonal?.toFixed(4), r.residual?.toFixed(4)].join(','))
        ].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `stl_${(result?.keyword || 'trends').replace(/\s+/g, '_')}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const decomp = result?.decomposition || [];
    const analytics = result?.seasonalAnalytics || {};

    return (
        <Layout>
            <div className="container-fluid py-4" style={{ maxWidth: '1400px', margin: '0 auto' }}>
                {/* ═══ HEADER ═══ */}
                <div className="text-center mb-4">
                    <h2 className="fw-bold mb-1 text-slate-800" style={{ fontSize: '1.75rem', color: '#0f172a' }}>Web Traffic</h2>
                    <p className="text-muted small mb-0">
                        Google Trends (US, last 5y) → STL Decomposition (Moving Avg) → Brand Analytics
                    </p>
                </div>

                {/* ═══ ATTENTION ALERT ═══ */}
                <div className="alert border-0 shadow-sm d-flex align-items-start mb-4" style={{ background: '#fffbeb', borderLeft: '4px solid var(--jc-orange) !important', borderRadius: '8px', color: '#b45309' }}>
                    <i className="bi bi-exclamation-triangle-fill fs-5 me-3" style={{ color: 'var(--jc-orange)' }}></i>
                    <div style={{ fontSize: '0.875rem', lineHeight: '1.5' }}>
                        <strong>Attention:</strong> Search interest popularity is relative and does not equal absolute search volume. Please cross-reference and confirm with Search Query Performance data.
                    </div>
                </div>

                {/* ═══ CONTROLS CARD ═══ */}
                <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '12px', background: '#ffffff' }}>
                    <div className="card-body p-4">
                        <div className="row g-3 align-items-end">
                            <div className="col-md-5">
                                <label className="form-label fw-semibold text-secondary small">Keyword (required for Request mode)</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g., rocket stove"
                                    value={keyword}
                                    onChange={e => setKeyword(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && handleRequest()}
                                />
                            </div>
                            <div className="col-md-auto">
                                <button className="btn btn-primary d-flex align-items-center gap-2 px-4 py-2" onClick={handleRequest} disabled={loading} style={{ height: '42px' }}>
                                    {loading ? (
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    ) : (
                                        <i className="bi bi-search"></i>
                                    )}
                                    Request Trends
                                </button>
                            </div>
                            <div className="col-md-auto ms-md-auto">
                                <div className="d-flex align-items-center gap-2">
                                    <label className="btn btn-outline-secondary d-flex align-items-center gap-2 mb-0 px-3" style={{ cursor: 'pointer', height: '42px', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                                        <i className="bi bi-filetype-csv fs-5"></i>
                                        Choose CSV
                                        <input type="file" accept=".csv,.tsv" hidden onChange={handleFileChange} />
                                    </label>
                                    {csvName && (
                                        <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-20 d-flex align-items-center gap-2 px-3 py-2" style={{ borderRadius: '8px', fontSize: '0.8rem' }}>
                                            <i className="bi bi-file-earmark-check-fill"></i>
                                            {csvName.length > 20 ? csvName.substring(0, 17) + '...' : csvName}
                                            <i className="bi bi-x-lg cursor-pointer" style={{ cursor: 'pointer' }} onClick={handleClearCsv}></i>
                                        </span>
                                    )}
                                    <button className="btn btn-outline-primary d-flex align-items-center gap-2 px-3" onClick={handleUploadCsv} disabled={loading || !csvFile} style={{ height: '42px' }}>
                                        <i className="bi bi-upload"></i>Upload CSV
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ═══ LOADING / ERROR ═══ */}
                {loading && (
                    <div className="text-center py-5">
                        <div className="spinner-border" style={{ color: 'var(--jc-orange)' }} role="status" />
                        <p className="mt-2 text-muted small">Decomposing seasonal components and fetching Google Trends data…</p>
                    </div>
                )}
                {error && (
                    <div className="alert alert-danger border-0 shadow-sm d-flex align-items-center mb-4" role="alert" style={{ borderRadius: '8px', background: '#fef2f2', color: '#b91c1c' }}>
                        <i className="bi bi-exclamation-circle-fill fs-5 me-3"></i>
                        <div style={{ fontSize: '0.875rem' }}>{error}</div>
                    </div>
                )}

                {/* ═══ CHART 1: STL Decomposition (4 subplots) ═══ */}
                {decomp.length > 0 && (
                    <>
                        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '12px', background: '#ffffff' }}>
                            <div className="card-body p-4">
                                <Plot
                                    data={[
                                        { x: decomp.map(d => d.displayDate), y: decomp.map(d => d.originalValue), name: 'Original', mode: 'lines', xaxis: 'x', yaxis: 'y' },
                                        { x: decomp.map(d => d.displayDate), y: decomp.map(d => d.trend), name: 'Trend', mode: 'lines', xaxis: 'x2', yaxis: 'y2' },
                                        { x: decomp.map(d => d.displayDate), y: decomp.map(d => d.seasonal), name: 'Seasonal', mode: 'lines', xaxis: 'x3', yaxis: 'y3' },
                                        { x: decomp.map(d => d.displayDate), y: decomp.map(d => d.residual), name: 'Residual', mode: 'lines', xaxis: 'x4', yaxis: 'y4' },
                                    ]}
                                    layout={{
                                        title: {
                                            text: `<b>STL Decomposition</b> — <i>${result?.keyword || ''}</i>`,
                                            font: { family: 'Inter, system-ui, sans-serif', size: 16, color: '#0f172a' }
                                        },
                                        height: 750,
                                        grid: { rows: 4, columns: 1, pattern: 'independent', roworder: 'top to bottom' },
                                        xaxis: { anchor: 'y', matches: 'x4', showgrid: true, gridcolor: '#f1f5f9', linecolor: '#e2e8f0', tickfont: { family: 'Inter', size: 10, color: '#64748b' } },
                                        xaxis2: { anchor: 'y2', matches: 'x4', showgrid: true, gridcolor: '#f1f5f9', linecolor: '#e2e8f0', tickfont: { family: 'Inter', size: 10, color: '#64748b' } },
                                        xaxis3: { anchor: 'y3', matches: 'x4', showgrid: true, gridcolor: '#f1f5f9', linecolor: '#e2e8f0', tickfont: { family: 'Inter', size: 10, color: '#64748b' } },
                                        xaxis4: { anchor: 'y4', showgrid: true, gridcolor: '#f1f5f9', linecolor: '#e2e8f0', tickfont: { family: 'Inter', size: 10, color: '#64748b' } },
                                        yaxis: { title: { text: 'Original', font: { size: 11, color: '#475569', family: 'Inter' } }, domain: [0.78, 1.0], showgrid: true, gridcolor: '#f1f5f9', linecolor: '#e2e8f0', tickfont: { family: 'Inter', size: 10, color: '#64748b' } },
                                        yaxis2: { title: { text: 'Trend', font: { size: 11, color: '#475569', family: 'Inter' } }, domain: [0.53, 0.75], showgrid: true, gridcolor: '#f1f5f9', linecolor: '#e2e8f0', tickfont: { family: 'Inter', size: 10, color: '#64748b' } },
                                        yaxis3: { title: { text: 'Seasonal', font: { size: 11, color: '#475569', family: 'Inter' } }, domain: [0.28, 0.50], showgrid: true, gridcolor: '#f1f5f9', linecolor: '#e2e8f0', tickfont: { family: 'Inter', size: 10, color: '#64748b' } },
                                        yaxis4: { title: { text: 'Residual', font: { size: 11, color: '#475569', family: 'Inter' } }, domain: [0.0, 0.23], showgrid: true, gridcolor: '#f1f5f9', linecolor: '#e2e8f0', tickfont: { family: 'Inter', size: 10, color: '#64748b' } },
                                        shapes: [
                                            { type: 'line', xref: 'x', yref: 'paper', x0: decomp[0].displayDate, x1: decomp[0].displayDate, y0: 0, y1: 1, line: { dash: 'dot', color: 'rgba(0,0,0,0.06)', width: 1 } },
                                            { type: 'line', xref: 'x', yref: 'paper', x0: decomp[decomp.length - 1].displayDate, x1: decomp[decomp.length - 1].displayDate, y0: 0, y1: 1, line: { dash: 'dot', color: 'rgba(0,0,0,0.06)', width: 1 } },
                                            { type: 'line', xref: 'x3', yref: 'y3', x0: decomp[0].displayDate, x1: decomp[decomp.length - 1].displayDate, y0: 0, y1: 0, line: { dash: 'dash', color: '#cbd5e1', width: 1 } },
                                            { type: 'line', xref: 'x4', yref: 'y4', x0: decomp[0].displayDate, x1: decomp[decomp.length - 1].displayDate, y0: 0, y1: 0, line: { dash: 'dash', color: '#cbd5e1', width: 1 } },
                                        ],
                                        margin: { l: 60, r: 30, t: 60, b: 40 },
                                        plot_bgcolor: 'rgba(0,0,0,0)',
                                        paper_bgcolor: 'rgba(0,0,0,0)',
                                    }}
                                    config={{ responsive: true, displayModeBar: false }}
                                    useResizeHandler={true}
                                    style={{ width: '100%', height: '750px' }}
                                />
                                <div className="p-3 bg-light rounded-3 mt-3 border border-1 border-light-subtle">
                                    <p className="text-muted small mb-0">
                                        <strong>How to read this STL Decomposition:</strong><br />
                                        • <strong>Trend:</strong> Shows the clean, underlying long-term direction of demand without seasonal variations.<br />
                                        • <strong>Seasonal:</strong> Highlights the recurring cycles within each year (revealing product demand cycles).<br />
                                        • <strong>Residual:</strong> Isolates the noise, outliers, and short-term anomalies.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* ═══ Expandable data table ═══ */}
                        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '12px', background: '#ffffff', overflow: 'hidden' }}>
                            <div className="card-header border-0 bg-white d-flex align-items-center justify-content-between p-3" style={{ cursor: 'pointer' }} onClick={() => setShowTable(!showTable)}>
                                <h6 className="mb-0 fw-semibold text-slate-800 d-flex align-items-center gap-2">
                                    <i className={`bi bi-chevron-${showTable ? 'up' : 'down'} text-secondary`}></i>
                                    Show Decomposition Data Table
                                </h6>
                                <button className="btn btn-sm btn-outline-success d-flex align-items-center gap-1" onClick={(e) => { e.stopPropagation(); handleDownloadCsv(); }}>
                                    <i className="bi bi-download"></i>Download CSV
                                </button>
                            </div>
                            {showTable && (
                                <div className="card-body p-0 border-top">
                                    <div className="table-responsive" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                        <table className="table table-striped table-hover text-center align-middle mb-0" style={{ fontSize: '0.875rem' }}>
                                            <thead style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }} className="sticky-top">
                                                <tr style={{ color: '#475569', fontWeight: '600' }}>
                                                    <th className="py-3">Date</th>
                                                    <th className="py-3">Original Value</th>
                                                    <th className="py-3">Trend</th>
                                                    <th className="py-3">Seasonal</th>
                                                    <th className="py-3">Residual</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {decomp.map((row, i) => (
                                                    <tr key={i}>
                                                        <td className="py-2.5 fw-semibold text-secondary">{row.displayDate}</td>
                                                        <td className="py-2.5">{row.originalValue}</td>
                                                        <td className="py-2.5 text-primary">{row.trend?.toFixed(3)}</td>
                                                        <td className="py-2.5 text-success">{row.seasonal?.toFixed(3)}</td>
                                                        <td className="py-2.5 text-muted">{row.residual?.toFixed(3)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ═══ Additional seasonal analyses ═══ */}
                        <div className="d-flex align-items-center gap-2 mt-5 mb-4">
                            <i className="bi bi-bar-chart-steps fs-4 text-success" style={{ color: 'var(--jc-green)' }}></i>
                            <h3 className="fw-bold mb-0 text-slate-800" style={{ fontSize: '1.5rem', color: '#0f172a' }}>Seasonal Demand Insights</h3>
                        </div>

                        {/* ═══ CHART 2: Average seasonal pattern by ISO week ═══ */}
                        {analytics.weeklyPattern?.length > 0 && (
                            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '12px', background: '#ffffff' }}>
                                <div className="card-body p-4">
                                    {(() => {
                                        const weeklyX = analytics.weeklyPattern.map(d => d.isoWeek);
                                        const weeklyY = analytics.weeklyPattern.map(d => d.meanSeasonal);
                                        const weeklyText = weeklyX.map((w, i) => {
                                            const isKeyPoint = i === 0 || i === weeklyX.length - 1 || w % 8 === 0;
                                            return isKeyPoint ? `W${w}: ${Number(weeklyY[i]).toFixed(2)}` : '';
                                        });

                                        const weeklyMax = weeklyY.length
                                            ? weeklyY.reduce((best, v, i) => v > best.value ? { value: v, i } : best, { value: -Infinity, i: 0 })
                                            : null;
                                        const weeklyMin = weeklyY.length
                                            ? weeklyY.reduce((best, v, i) => v < best.value ? { value: v, i } : best, { value: Infinity, i: 0 })
                                            : null;

                                        return (
                                            <Plot
                                                data={[{
                                                    x: weeklyX,
                                                    y: weeklyY,
                                                    mode: 'lines+markers+text',
                                                    name: 'Mean Seasonal',
                                                    line: { color: '#636EFA' },
                                                    marker: { size: 6, color: '#636EFA' },
                                                    text: weeklyText,
                                                    textposition: 'top center',
                                                    textfont: { size: 10, color: '#1f2937' },
                                                    cliponaxis: false,
                                                    hovertemplate: 'Week %{x}<br>Seasonal: %{y:.2f}<extra></extra>',
                                                }]}
                                                layout={{
                                                    title: {
                                                        text: '<b>Average Seasonal Pattern by ISO Week (1–53)</b>',
                                                        font: { family: 'Inter, system-ui, sans-serif', size: 14, color: '#334155' }
                                                    },
                                                    height: 350,
                                                    xaxis: { title: 'ISO Week (1–53)', dtick: 4, gridcolor: '#f1f5f9', linecolor: '#e2e8f0', tickfont: { family: 'Inter', size: 10, color: '#64748b' } },
                                                    yaxis: { title: 'Seasonal Value', gridcolor: '#f1f5f9', linecolor: '#e2e8f0', tickfont: { family: 'Inter', size: 10, color: '#64748b' } },
                                                    shapes: [{ type: 'line', xref: 'paper', x0: 0, x1: 1, y0: 0, y1: 0, line: { dash: 'dash', color: 'gray' } }],
                                                    annotations: [
                                                        ...(weeklyMax ? [{
                                                            x: weeklyX[weeklyMax.i],
                                                            y: weeklyY[weeklyMax.i],
                                                            text: `Peak: W${weeklyX[weeklyMax.i]} (${Number(weeklyY[weeklyMax.i]).toFixed(2)})`,
                                                            showarrow: true,
                                                            arrowhead: 2,
                                                            ax: 20,
                                                            ay: -25,
                                                            bgcolor: 'rgba(209, 250, 229, 0.9)',
                                                            bordercolor: '#10b981',
                                                            borderwidth: 1,
                                                            font: { size: 10, color: '#065f46', family: 'Inter' },
                                                        }] : []),
                                                        ...(weeklyMin ? [{
                                                            x: weeklyX[weeklyMin.i],
                                                            y: weeklyY[weeklyMin.i],
                                                            text: `Low: W${weeklyX[weeklyMin.i]} (${Number(weeklyY[weeklyMin.i]).toFixed(2)})`,
                                                            showarrow: true,
                                                            arrowhead: 2,
                                                            ax: -20,
                                                            ay: 25,
                                                            bgcolor: 'rgba(254, 226, 226, 0.9)',
                                                            bordercolor: '#ef4444',
                                                            borderwidth: 1,
                                                            font: { size: 10, color: '#991b1b', family: 'Inter' },
                                                        }] : []),
                                                    ],
                                                    margin: { l: 60, r: 30, t: 50, b: 50 },
                                                    plot_bgcolor: 'rgba(0,0,0,0)',
                                                    paper_bgcolor: 'rgba(0,0,0,0)',
                                                }}
                                                config={{ responsive: true, displayModeBar: false }}
                                                useResizeHandler={true}
                                                style={{ width: '100%', height: '350px' }}
                                            />
                                        );
                                    })()}
                                    <p className="text-muted small mt-2 mb-0">
                                        <em>Interpretation:</em> this line shows the <strong>average seasonal effect</strong> at each ISO week across all years.
                                        Peaks indicate weeks that are typically above the baseline; troughs indicate below-baseline weeks.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* ═══ CHART 3: Average seasonal pattern by month ═══ */}
                        {analytics.monthlyPattern?.length > 0 && (
                            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '12px', background: '#ffffff' }}>
                                <div className="card-body p-4">
                                    {(() => {
                                        const monthlyX = analytics.monthlyPattern.map(d => d.monthLabel);
                                        const monthlyY = analytics.monthlyPattern.map(d => d.meanSeasonal);
                                        const monthlyText = monthlyX.map((m, i) => `${m}: ${Number(monthlyY[i]).toFixed(2)}`);

                                        return (
                                            <Plot
                                                data={[{
                                                    x: monthlyX,
                                                    y: monthlyY,
                                                    mode: 'lines+markers+text',
                                                    name: 'Mean Seasonal',
                                                    marker: { size: 8 },
                                                    line: { color: '#EF553B' },
                                                    text: monthlyText,
                                                    textposition: 'top center',
                                                    textfont: { size: 10, color: '#1f2937', family: 'Inter' },
                                                    cliponaxis: false,
                                                    hovertemplate: 'Month %{x}<br>Seasonal: %{y:.2f}<extra></extra>',
                                                }]}
                                                layout={{
                                                    title: {
                                                        text: '<b>Average Seasonal Pattern by Month</b>',
                                                        font: { family: 'Inter, system-ui, sans-serif', size: 14, color: '#334155' }
                                                    },
                                                    height: 350,
                                                    xaxis: { title: 'Month', gridcolor: '#f1f5f9', linecolor: '#e2e8f0', tickfont: { family: 'Inter', size: 10, color: '#64748b' } },
                                                    yaxis: { title: 'Seasonal Value', gridcolor: '#f1f5f9', linecolor: '#e2e8f0', tickfont: { family: 'Inter', size: 10, color: '#64748b' } },
                                                    shapes: [{ type: 'line', xref: 'paper', x0: 0, x1: 1, y0: 0, y1: 0, line: { dash: 'dash', color: 'gray' } }],
                                                    margin: { l: 60, r: 30, t: 50, b: 50 },
                                                    plot_bgcolor: 'rgba(0,0,0,0)',
                                                    paper_bgcolor: 'rgba(0,0,0,0)',
                                                }}
                                                config={{ responsive: true, displayModeBar: false }}
                                                useResizeHandler={true}
                                                style={{ width: '100%', height: '350px' }}
                                            />
                                        );
                                    })()}
                                    <p className="text-muted small mt-2 mb-0">
                                        <em>Interpretation:</em> this summarizes the <strong>typical monthly seasonality</strong>.
                                        Use it to spot which months are usually stronger or weaker relative to the yearly baseline.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* ═══ CHART 4: Seasonal distribution by month (box plot) ═══ */}
                        {analytics.monthlyDistribution?.length > 0 && (
                            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '12px', background: '#ffffff' }}>
                                <div className="card-body p-4">
                                    <Plot
                                        data={analytics.monthlyDistribution.map(m => ({
                                            y: m.values,
                                            type: 'box',
                                            name: m.monthLabel,
                                            boxpoints: 'outliers',
                                        }))}
                                        layout={{
                                            title: {
                                                text: '<b>Seasonal Distribution by Month</b>',
                                                font: { family: 'Inter, system-ui, sans-serif', size: 14, color: '#334155' }
                                            },
                                            height: 400,
                                            xaxis: { title: 'Month', gridcolor: '#f1f5f9', linecolor: '#e2e8f0', tickfont: { family: 'Inter', size: 10, color: '#64748b' } },
                                            yaxis: { title: 'Seasonal Value', gridcolor: '#f1f5f9', linecolor: '#e2e8f0', tickfont: { family: 'Inter', size: 10, color: '#64748b' } },
                                            shapes: [{ type: 'line', xref: 'paper', x0: 0, x1: 1, y0: 0, y1: 0, line: { dash: 'dash', color: 'gray' } }],
                                            showlegend: false,
                                            margin: { l: 60, r: 30, t: 50, b: 50 },
                                            plot_bgcolor: 'rgba(0,0,0,0)',
                                            paper_bgcolor: 'rgba(0,0,0,0)',
                                        }}
                                        config={{ responsive: true, displayModeBar: false }}
                                        useResizeHandler={true}
                                        style={{ width: '100%', height: '400px' }}
                                    />
                                    <p className="text-muted small mt-2 mb-0">
                                        <em>Interpretation:</em> boxes show the <strong>spread of seasonal values</strong> for each month across years.
                                        Wider boxes or longer whiskers mean more variability; outliers capture unusual months.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* ═══ CHART 5: Year-over-year seasonality comparison ═══ */}
                        {analytics.yearOverYear?.length > 0 && (() => {
                            const years = [...new Set(analytics.yearOverYear.map(d => d.year))].sort();
                            const colors = ['#636EFA', '#EF553B', '#00CC96', '#AB63FA', '#FFA15A'];
                            return (
                                <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '12px', background: '#ffffff' }}>
                                    <div className="card-body p-4">
                                        <Plot
                                            data={years.map((yr, idx) => {
                                                const points = analytics.yearOverYear.filter(d => d.year === yr);
                                                const xVals = points.map(d => d.isoWeek);
                                                const yVals = points.map(d => d.seasonal);
                                                const textVals = xVals.map((_, i) =>
                                                    i === xVals.length - 1 ? `${yr}: ${Number(yVals[i]).toFixed(2)}` : ''
                                                );

                                                return {
                                                    x: xVals,
                                                    y: yVals,
                                                    mode: 'lines+markers+text',
                                                    name: String(yr),
                                                    line: { color: colors[idx % colors.length] },
                                                    marker: { size: 5 },
                                                    text: textVals,
                                                    textposition: 'middle right',
                                                    textfont: { size: 10, color: '#111827', family: 'Inter' },
                                                    cliponaxis: false,
                                                    hovertemplate: `Year ${yr}<br>Week %{x}<br>Seasonal: %{y:.2f}<extra></extra>`,
                                                };
                                            })}
                                            layout={{
                                                title: {
                                                    text: `<b>Seasonality Compared by Year (${years[0]}–${years[years.length - 1]})</b>`,
                                                    font: { family: 'Inter, system-ui, sans-serif', size: 14, color: '#334155' }
                                                },
                                                height: 400,
                                                xaxis: { title: 'ISO Week', dtick: 4, gridcolor: '#f1f5f9', linecolor: '#e2e8f0', tickfont: { family: 'Inter', size: 10, color: '#64748b' } },
                                                yaxis: { title: 'Seasonal Value', gridcolor: '#f1f5f9', linecolor: '#e2e8f0', tickfont: { family: 'Inter', size: 10, color: '#64748b' } },
                                                shapes: [{ type: 'line', xref: 'paper', x0: 0, x1: 1, y0: 0, y1: 0, line: { dash: 'dash', color: 'gray' } }],
                                                margin: { l: 60, r: 30, t: 50, b: 50 },
                                                plot_bgcolor: 'rgba(0,0,0,0)',
                                                paper_bgcolor: 'rgba(0,0,0,0)',
                                            }}
                                            config={{ responsive: true, displayModeBar: false }}
                                            useResizeHandler={true}
                                            style={{ width: '100%', height: '400px' }}
                                        />
                                        <p className="text-muted small mt-2 mb-0">
                                            <em>Interpretation:</em> this compares <strong>seasonal curves across recent years</strong> on the same ISO-week axis.
                                            Look for alignment (stable seasonality) or divergences (shifts in timing or magnitude).
                                        </p>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* ═══ Footer ═══ */}
                        <div className="text-center mt-5 mb-3">
                            <p className="text-muted small" style={{ fontSize: '0.75rem' }}>
                                Data source: Google Trends API • Decomposition method: STL (Moving Average) • Visualizations: Plotly Responsive JS
                            </p>
                        </div>
                    </>
                )}
            </div>
        </Layout>
    );
}

export default WebTraffic;
